import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Alert,
  Button,
  Empty,
  Select,
  Skeleton,
  Space,
  Spin,
  Switch,
  Tabs,
  Tooltip,
  message,
} from "antd";
import { FileExcelOutlined, ReloadOutlined } from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";
import DeviationKpiCards from "./DeviationKpiCards";
import DeviationSummaryTable from "./DeviationSummaryTable";
import {
  DeviationSummaryData,
  DeviationTab,
  DeviationTabKey,
  TAB_META,
} from "./deviationShared";

// 3M Deviation Summary — per-resource rate register for Material / Manpower /
// Machinery: what the line was priced at (BOQ Rate), what the current norm
// master says it should cost (Target Rate), what it is actually costing
// (Actual Rate), and what the gap is worth over the allocated quantity.
//
// Project context comes the same way as the other reports: location.state
// { id, projectName } with the :projectId route param as fallback.

const TAB_ORDER: DeviationTabKey[] = ["MATERIAL", "MANPOWER", "MACHINERY"];

const ThreeMDeviationSummaryReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<DeviationSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<DeviationTabKey>("MATERIAL");
  // The Norm Δ pair (Target vs BOQ) is on by default because only manpower
  // OTHERS and machinery TOOLS can produce a true Actual-vs-BOQ deviation —
  // without it the Deviation column is an em dash on most rows.
  const [showNormDelta, setShowNormDelta] = useState(true);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);

  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter options: one unfiltered consolidation load per project (the
  // deviation payload carries no phase/activity info, same as the Cost
  // Analysis reports).
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) => {
        // Non-fatal: the report still renders, only the filters stay empty.
        console.error("Error loading phase/activity filter options:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // One call for all three tabs: they share a filter set, so fetching per tab
  // could render tabs computed under different filter states.
  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.get3MDeviationSummary(projectId, {
          phaseId,
          activityId,
        });
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load deviation summary");
          message.error(response?.message || "Failed to load deviation summary");
        }
      } catch (err) {
        console.error("Error loading 3M Deviation Summary:", err);
        if (!cancelled) {
          setError("Error loading 3M Deviation Summary");
          message.error("Error loading 3M Deviation Summary");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";

  const tabData = useMemo<Record<DeviationTabKey, DeviationTab | undefined>>(
    () => ({
      MATERIAL: data?.material,
      MANPOWER: data?.manpower,
      MACHINERY: data?.machinery,
    }),
    [data]
  );

  const wbsName = useWbsName();
  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases, wbsName]
  );

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId;
  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
  };

  const totalRows = TAB_ORDER.reduce(
    (sum, key) => sum + (tabData[key]?.rowCount || 0),
    0
  );

  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)
              ?.activityName
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        orgName,
        logo,
        filters: { phaseName, activityName },
      });
      message.success("Excel exported");
    } catch (err) {
      console.error("Error exporting 3M Deviation Summary:", err);
      message.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const renderTab = (key: DeviationTabKey) => {
    const tab = tabData[key];
    const rows = tab?.rows || [];
    return (
      <>
        <DeviationKpiCards tab={tab} showNormDelta={showNormDelta} />
        {rows.length === 0 ? (
          <Empty
            description={
              hasFilters
                ? `No ${TAB_META[key].label.toLowerCase()} items match the current filters`
                : `No ${TAB_META[key].label.toLowerCase()} allocations for this project yet`
            }
            style={{ padding: "40px 0" }}
          />
        ) : (
          <DeviationSummaryTable
            tab={key}
            rows={rows}
            showNormDelta={showNormDelta}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Head title="3M Deviation Summary" />
      <Content>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  3M Deviation Summary
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · BOQ vs target vs actual rate, per resource
                </span>
              </div>
              <Space>
                <Tooltip title="Show the Target-vs-BOQ pair (how far the current norm master has moved from the planned rates)">
                  <Space size={6}>
                    <span style={{ fontSize: 12.5, color: "#8094ae" }}>
                      Norm Δ
                    </span>
                    <Switch
                      size="small"
                      checked={showNormDelta}
                      onChange={setShowNormDelta}
                    />
                  </Space>
                </Tooltip>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || totalRows === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                {/* Says out loud why so many cells are dashes, so a sparse
                    report reads as a capture gap rather than a broken one. */}
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 14 }}
                  message="How to read this report"
                  description={
                    <span style={{ fontSize: 12.5 }}>
                      <b>BOQ Rate</b> is what each line was priced at when planned.{" "}
                      <b>Target Rate</b> is today's norm-master rate.{" "}
                      <b>Actual Rate</b> comes from site purchase averages
                      (material) or approved time logs (manpower &amp; machinery).
                      A dash means that rate cannot be measured for that row — hover
                      it to see why. Impact totals sum only the measurable rows, so
                      always read them against the coverage tile. On{" "}
                      <b>Manpower</b> and <b>Machinery</b>, expand any item to see
                      the <b>activities and tasks</b> it was applied to — the BOQ
                      rate is per task, so that is where a deviation shows whether
                      it is one face or all of them. Machinery totals fold{" "}
                      <b>mucking, tools and equipment</b> together; the three
                      columns before Planned Cost show the split.
                    </span>
                  }
                />
                <Tabs
                  activeKey={activeTab}
                  onChange={(key) => setActiveTab(key as DeviationTabKey)}
                  items={TAB_ORDER.map((key) => ({
                    key,
                    label: (
                      <span>
                        {TAB_META[key].label}
                        <span style={{ color: "#8094ae", marginLeft: 6 }}>
                          {tabData[key]?.rowCount || 0}
                        </span>
                      </span>
                    ),
                    children: renderTab(key),
                  }))}
                />
              </Spin>
            )}
          </div>
        </Card>
      </Content>
    </>
  );
};

export default ThreeMDeviationSummaryReport;
